import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Select,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import useAxios from "axios-hooks";
import { useSearchParams } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import {
  FaCheckCircle,
  FaSyncAlt,
  FaCalendarAlt,
  FaBolt,
  FaTimesCircle,
} from "react-icons/fa";

import { toastState } from "atoms/toastState";
import logger from "infra/logger";
import PayoutConfigTenantsList from "../components/PayoutConfigTenantsList";
import PayoutConfigChargersTable from "../components/PayoutConfigChargersTable";
import PayoutConfigModal from "../components/PayoutConfigModal";
import StatCard from "../components/StatCard";

const MotionBox = motion(Box);

const MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

// Year options: current year and previous 3 years
const YEAR_OPTIONS = (() => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = 0; i <= 3; i++) years.push(currentYear - i);
  return years;
})();

// Configurations tab: a single place to view tenants, their chargers, and
// each charger's payout configuration. Master/detail layout.
//
// Data comes from a single optimized endpoint that returns every tenant with
// their chargers and each charger's ChargerPayoutConfig embedded, so no
// per-charger fetching is required — the status column is accurate immediately.
const Configurations = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Month/year from query params, defaulting to current month/year
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const m = searchParams.get("month");
    return m ? parseInt(m) : new Date().getMonth() + 1;
  });
  const [selectedYear, setSelectedYear] = useState(() => {
    const y = searchParams.get("year");
    return y ? parseInt(y) : new Date().getFullYear();
  });

  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [modalState, setModalState] = useState({
    isOpen: false,
    station: null,
    existingConfig: null,
  });

  const subTextColor = useColorModeValue("gray.500", "gray.400");
  const summaryBg = useColorModeValue(
    "rgba(255, 255, 255, 0.6)",
    "rgba(26, 32, 44, 0.6)",
  );
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const inputBg = useColorModeValue("white", "whiteAlpha.100");

  // Persist month/year to URL query params
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("month", selectedMonth);
    next.set("year", selectedYear);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear]);

  // If the selected month becomes a future month (when current year is selected),
  // reset it to the current month
  useEffect(() => {
    if (isFutureMonth(selectedMonth)) {
      setSelectedMonth(currentMonth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  const [{ loading }, fetchTenantsChargers] = useAxios(
    {
      method: "GET",
      url: `/api/tenant/chargers/payoutConfig?month=${selectedMonth}&year=${selectedYear}`,
    },
    { manual: true },
  );

  const [{ loading: regenerateLoading }, regenerateConfigs] = useAxios(
    {
      method: "GET",
      url: `/api/stations/payoutConfig/regenerate?month=${selectedMonth}&year=${selectedYear}`,
    },
    { manual: true },
  );

  const setToast = useSetRecoilState(toastState);

  // Current month/year — used to disable future months in the filter
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();
  const isFutureMonth = (monthValue) =>
    selectedYear === currentYear && monthValue > currentMonth;

  const loadData = useCallback(async () => {
    try {
      const response = await fetchTenantsChargers();
      const data = response.data?.data || [];
      setTenants(data);
      if (data.length > 0) {
        const firstEnabled = data.find((t) => t.PayoutsEnabled === true);
        setSelectedTenantId((firstEnabled || data[0]).Id);
      } else {
        setSelectedTenantId(null);
      }
    } catch (error) {
      logger.log(error);
    }
  }, [fetchTenantsChargers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRegenerate = useCallback(async () => {
    try {
      await regenerateConfigs();
      setToast({
        isOpen: true,
        title: "Configurations regenerated successfully",
        status: "success",
        duration: 5000,
      });
      loadData();
    } catch (error) {
      logger.error("Error regenerating configs:", error);
      setToast({
        isOpen: true,
        title: "Error regenerating configurations",
        status: "error",
        duration: 5000,
      });
    }
  }, [regenerateConfigs, loadData, setToast]);

  const selectedTenant = useMemo(
    () => tenants.find((t) => t.Id === selectedTenantId) || null,
    [tenants, selectedTenantId],
  );

  const chargers = useMemo(
    () => selectedTenant?.chargers || [],
    [selectedTenant],
  );

  // Per-tenant summary (for the currently selected tenant).
  const summary = useMemo(() => {
    const total = chargers.length;
    const configured = chargers.filter((c) => c.ChargerPayoutConfig).length;
    return { total, configured, missing: total - configured };
  }, [chargers]);

  const handleToggleRow = useCallback((station) => {
    setExpandedRowId((prev) => (prev === station.Id ? null : station.Id));
  }, []);

  const handleEdit = useCallback((station, existingConfig) => {
    setModalState({ isOpen: true, station, existingConfig });
  }, []);

  // After a create/update, replace the charger's ChargerPayoutConfig in place
  // using the payload returned by the update API.
  const handleConfigSaved = useCallback((station, config) => {
    setTenants((prev) =>
      prev.map((tenant) => {
        if (tenant.Id !== station.TenantId) return tenant;
        return {
          ...tenant,
          chargers: (tenant.chargers || []).map((charger) =>
            charger.Id === station.Id
              ? { ...charger, ChargerPayoutConfig: config }
              : charger,
          ),
        };
      }),
    );
  }, []);

  const closeModal = useCallback(() => {
    setModalState({ isOpen: false, station: null, existingConfig: null });
  }, []);

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      w="100%"
    >
      {/* Summary + month/year filter */}
      <Box mb={5}>
        <Flex
          p={4}
          bg={summaryBg}
          backdropFilter="blur(16px)"
          borderRadius="2xl"
          border="1px solid"
          borderColor="rgba(245, 125, 54, 0.15)"
          boxShadow="0 8px 32px rgba(31, 38, 135, 0.06)"
          wrap="wrap"
          gap={4}
          align="center"
          justify="space-between"
        >
          <HStack spacing={3} flexWrap="wrap">
            <StatCard
              icon={FaBolt}
              label="Chargers"
              value={summary.total}
              accent="#f57d36"
              bg="rgba(245, 125, 54, 0.12)"
            />
            <StatCard
              icon={FaCheckCircle}
              label="Configured"
              value={summary.configured}
              accent="#38A169"
              bg="rgba(56, 161, 105, 0.12)"
            />
            <StatCard
              icon={FaTimesCircle}
              label="Not Set"
              value={summary.missing}
              accent="#E53E3E"
              bg="rgba(229, 62, 62, 0.12)"
            />
          </HStack>

          {/* Month / Year filter */}
          <HStack spacing={2.5} flexShrink={0} align="center">
            <HStack spacing={1.5} display={{ base: "none", lg: "flex" }}>
              <Icon as={FaCalendarAlt} color="#f57d36" boxSize={3.5} />
              <Text fontSize="xs" fontWeight="semibold" color={subTextColor} textTransform="uppercase" letterSpacing="wider">
                Period
              </Text>
            </HStack>
            <Select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              w="180px"
              bg={inputBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="md"
              size="sm"
              _focus={{
                borderColor: "#f57d36",
                boxShadow: "0 0 0 1px rgba(245, 125, 54, 0.3)",
              }}
            >
              {MONTH_OPTIONS.map((month) => {
                const future = isFutureMonth(month.value);
                return (
                  <option
                    key={month.value}
                    value={month.value}
                    disabled={future}
                    style={future ? { color: "gray" } : undefined}
                  >
                    {month.label}
                  </option>
                );
              })}
            </Select>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              w="140px"
              bg={inputBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="md"
              size="sm"
              _focus={{
                borderColor: "#f57d36",
                boxShadow: "0 0 0 1px rgba(245, 125, 54, 0.3)",
              }}
            >
              {YEAR_OPTIONS.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Select>
            <Button
              leftIcon={<Icon as={FaSyncAlt} boxSize={3.5} />}
              bgColor="#f57d36"
              color="white"
              _hover={{
                bgColor: "#e86d26",
                transform: "translateY(-2px)",
                boxShadow: "md",
              }}
              _active={{ bgColor: "#d55d16" }}
              size="sm"
              borderRadius="md"
              onClick={handleRegenerate}
              isLoading={regenerateLoading}
              loadingText="Regenerating..."
              fontWeight="semibold"
            >
              Regenerate Configuration
            </Button>
          </HStack>
        </Flex>
      </Box>

      {/* Master / detail */}
      <Flex
        direction={{ base: "column", md: "row" }}
        gap={5}
        align="stretch"
        w="100%"
      >
        <PayoutConfigTenantsList
          tenants={tenants}
          selectedTenantId={selectedTenantId}
          onSelectTenant={(tenant) => setSelectedTenantId(tenant.Id)}
          loading={loading}
        />

        <Box flex={1} minW={0}>
          <PayoutConfigChargersTable
            tenant={selectedTenant}
            chargers={chargers}
            loading={loading}
            expandedRowId={expandedRowId}
            onToggleRow={handleToggleRow}
            onEdit={handleEdit}
          />
        </Box>
      </Flex>

      <PayoutConfigModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        station={modalState.station}
        existingConfig={modalState.existingConfig}
        month={selectedMonth}
        year={selectedYear}
        onSaved={(config) =>
          modalState.station && handleConfigSaved(modalState.station, config)
        }
      />
    </MotionBox>
  );
};

export default Configurations;
